import dynamic from 'next/dynamic';
import { Spacer } from 'vcc-ui';

import { ContentWrapper, H2 } from '@collab/atoms';
import { PageTitle } from '@collab/molecules';

import Tag from 'atoms/Tag/Tag';
import DownloadSpecButton from 'features/swagger/DownloadSpecButton';
import Swagger from 'features/swagger/Swagger';
import SwaggerProvider from 'features/swagger/SwaggerProvider';
import PageSeo from 'libs/seo/PageSeo';
import Versions from 'molecules/Versions/Versions';
import ContributeAside from 'organisms/ContributeAside/ContributeAside';
import { ApiSpecPageProps } from 'types/pages';
import { usesAuthorizationCodeFlow } from 'utils/resourceChecks';

const TryItOut = dynamic(() => import('features/swagger/TryItOut'));

const VersioningWarning = dynamic(() => import('molecules/VersioningWarning'));

const pageTitle = 'Specification';

const ApiSpecPage: React.FC<ApiSpecPageProps> = ({
  config,
  openApiSpec,
  versions,
  isLatestVersion,
}) => {
  const { title, id: apiId, azureId, apiAuthMethod } = config;
  const { info, openapi } = openApiSpec;
  const isCoVeV1 = azureId === 'connected-vehicle-c3-v1';

  const versionDeprecationDate = isCoVeV1 ? `April 30, 2024` : undefined;

  return (
    <ContentWrapper>
      <PageSeo
        title={pageTitle}
        category={[title]}
        description={config.seoDescription}
      />
      {!isLatestVersion && (
        <VersioningWarning
          versions={versions}
          deprecationDate={versionDeprecationDate}
        />
      )}
      <PageTitle
        title={pageTitle}
        sticky
        versionsComponent={<Versions versions={versions} />}
        category={['APIs', title]}
      />
      <Spacer size={[6, 4]} />
      <H2>OpenAPI</H2>
      <Tag size="large">{`OAS ${openapi}`}</Tag>
      <p className="text-secondary mt-24">{info.description}</p>
      <div className="my-24" style={{ maxWidth: 'fit-content' }}>
        <DownloadSpecButton apiId={apiId} openApiSpec={openApiSpec} />
      </div>
      <Spacer />
      <SwaggerProvider openApiSpec={openApiSpec}>
        {usesAuthorizationCodeFlow(apiAuthMethod) && (
          <>
            <TryItOut apiId={apiId} apiTitle={title} />
            <Spacer size={4} />
          </>
        )}
        <Swagger />
      </SwaggerProvider>
      <ContributeAside />
    </ContentWrapper>
  );
};

export default ApiSpecPage;
